import { useState } from "react";
import { useTranslation } from "react-i18next";
import { campaigns } from "../../../mocks/campaigns";

interface CouponModalProps {
  code: string;
  title: string;
  onClose: () => void;
}

function CouponModal({ code, title, onClose }: CouponModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-anthracite-900/60 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl p-8 max-w-sm w-full text-center animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-14 h-14 rounded-full bg-forest-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-coupon-3-line text-2xl text-forest-600" />
        </div>
        <h3 className="font-display font-bold text-anthracite-900 text-xl mb-1">{title}</h3>
        <p className="text-anthracite-400 text-sm font-body mb-6">Kupon kodunuzu kopyalayın ve kullanın.</p>

        <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl p-3 mb-4">
          <span className="flex-1 font-display font-bold text-anthracite-900 text-lg tracking-widest">
            {code}
          </span>
          <button
            onClick={handleCopy}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold font-body transition-colors cursor-pointer whitespace-nowrap ${
              copied ? "bg-forest-600 text-white" : "bg-anthracite-900 text-white hover:bg-anthracite-700"
            }`}
          >
            {copied ? "Kopyalandı!" : "Kopyala"}
          </button>
        </div>

        <button
          onClick={onClose}
          className="text-sm text-anthracite-400 hover:text-anthracite-600 font-body cursor-pointer"
        >
          Kapat
        </button>
      </div>
    </div>
  );
}

export default function CampaignsSection() {
  const { t } = useTranslation();
  const [selectedCoupon, setSelectedCoupon] = useState<{ code: string; title: string } | null>(null);

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold font-body uppercase tracking-wider mb-4">
              {t("campaigns_badge")}
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-anthracite-900">
              {t("campaigns_title")}
            </h2>
            <p className="text-anthracite-400 text-base mt-2 font-body max-w-xl">
              {t("campaigns_subtitle")}
            </p>
          </div>
          <button className="inline-flex items-center gap-2 text-sm font-semibold text-forest-700 hover:text-forest-600 font-body whitespace-nowrap cursor-pointer">
            {t("campaigns_view_all")}
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        {/* Campaigns Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="group bg-white border border-stone-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform cursor-pointer"
            >
              {/* Image + discount overlay */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={campaign.image}
                  alt={campaign.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                {/* Discount badge */}
                <div className="absolute top-3 right-3 w-14 h-14 rounded-full bg-amber-500 flex flex-col items-center justify-center">
                  <span className="text-white font-display font-bold text-sm leading-none">
                    %{campaign.discountPct}
                  </span>
                  <span className="text-white/80 text-xs font-body">
                    {t("campaigns_discount")}
                  </span>
                </div>
                {campaign.isSponsored && (
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold font-body">
                    {t("campaigns_sponsored")}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-xs font-semibold text-forest-600 font-body">{campaign.venueCategory}</span>
                  <span className="text-stone-300">·</span>
                  <span className="text-xs text-stone-400 font-body">{campaign.district}</span>
                </div>
                <h3 className="font-display font-bold text-anthracite-900 text-sm mb-1 leading-snug">
                  {campaign.title}
                </h3>
                <p className="text-xs text-stone-500 font-body mb-1">{campaign.venueName}</p>
                <p className="text-xs text-anthracite-400 font-body leading-relaxed mb-4 line-clamp-2">
                  {campaign.description}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                  <div className="flex items-center gap-1 text-xs text-stone-400 font-body">
                    <i className="ri-calendar-line text-xs" />
                    <span>{t("campaigns_expires")}: {campaign.expiresDate}</span>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedCoupon({ code: campaign.couponCode, title: campaign.title })}
                  className="w-full mt-3 py-2 bg-anthracite-900 hover:bg-anthracite-700 text-white text-xs font-semibold font-body rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("campaigns_get_coupon")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedCoupon && (
        <CouponModal
          code={selectedCoupon.code}
          title={selectedCoupon.title}
          onClose={() => setSelectedCoupon(null)}
        />
      )}
    </section>
  );
}
